import React from 'react';
import { useMediaQuery } from '../hooks/useMediaQuery';

export default function FeaturesSection() {
  const m = useMediaQuery('(max-width: 767px)');

  /* Six cards — journey first, then care team, clinical + cost, emotional support. */
  const features = [
    {
      id: 'journey',
      emoji: '🗺️',
      iconBg: 'rgb(238, 237, 254)',
      borderLeft: '3px solid rgb(238, 237, 254)',
      title: 'Journey tracker &\nmilestone alerts',
      desc: "When you're TTC, fertile timing and cycles sit with labs and visits from consult through beta — one mapped timeline instead of scattered tools.",
    },
    {
      id: 'doctors',
      emoji: '🏥',
      iconBg: 'rgb(230, 241, 251)',
      borderLeft: '3px solid rgb(230, 241, 251)',
      title: 'Doctor &\nspecialist finder',
      desc: 'REI, OB, or reproductive immunologist — Nova matches you by chapter, location, and insurance. Less searching, more doing.',
    },
    {
      id: 'meds',
      emoji: '💉',
      iconBg: 'rgb(238, 237, 254)',
      borderLeft: '3px solid rgb(238, 237, 254)',
      title: 'IVF medication\nprotocol',
      desc: 'Save your exact protocol — doses, timing, and notes. Get reminders before every injection so nothing ever falls through.',
    },
    {
      id: 'labs',
      emoji: '🧪',
      iconBg: 'rgb(225, 245, 238)',
      borderLeft: '3px solid rgb(225, 245, 238)',
      title: 'Lab result\ninterpreter',
      desc: 'AMH, FSH, estradiol, progesterone — Nova explains what your numbers mean for your cycle and what to ask your RE next.',
    },
    {
      id: 'financial',
      emoji: '💰',
      iconBg: 'rgb(255, 241, 221)',
      borderLeft: '3px solid rgb(255, 241, 221)',
      title: 'IVF financial\ntracker',
      desc: 'Track every cycle cost — medications, monitoring, and procedures. Know your true spend and plan what comes next.',
    },
    {
      id: 'care',
      emoji: '💗',
      iconBg: 'rgb(255, 241, 221)',
      borderLeft: '3px solid rgb(255, 241, 221)',
      title: 'The harder roads,\nheld with care',
      desc: 'Recurrent loss, failed cycles, the grief between attempts — Nova meets you there with the right support and real answers.',
    },
  ];

  const sectionPad = { padding: m ? '48px 18px' : '64px 48px' };

  return (
    <section
      id="features"
      style={{
        ...sectionPad,
        backgroundColor: 'rgb(255, 252, 249)',
        scrollMarginTop: 88,
      }}
    >
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: m ? '0 6px' : 0, boxSizing: 'border-box' }}>
        <p
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.1em',
            color: 'var(--muted)',
            textTransform: 'uppercase',
            textAlign: 'center',
            marginBottom: 12,
          }}
        >
          What&apos;s included
        </p>

        <h2
          className="serif"
          style={{
            fontSize: m ? 'clamp(24px, 6vw, 28px)' : 'clamp(28px, 3vw, 32px)',
            fontWeight: 700,
            textAlign: 'center',
            color: 'var(--text)',
            lineHeight: 1.25,
            marginBottom: 12,
          }}
        >
          Built like a clinic.
          <br />
          <em style={{ color: 'var(--pink)', fontStyle: 'italic' }}>Reads like a friend.</em>
        </h2>

        <p
          style={{
            fontSize: 15,
            color: 'var(--muted)',
            textAlign: 'center',
            maxWidth: 560,
            margin: '0 auto 48px',
            lineHeight: 1.65,
          }}
        >
          Every appointment, every cycle, every hard day stays with you — already remembered, already current, every time you
          come back. So when the hard moment comes, you don&apos;t have to start by explaining where you are.
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: m ? '1fr' : 'repeat(3, minmax(0, 1fr))',
            gap: m ? 12 : 14,
          }}
        >
          {features.map((f) => (
            <div
              key={f.id}
              style={{
                background: 'rgb(255, 253, 249)',
                borderRadius: 14,
                padding: 22,
                display: 'flex',
                flexDirection: 'column',
                borderTop: '1px solid rgb(232, 224, 216)',
                borderRight: '1px solid rgb(232, 224, 216)',
                borderBottom: '1px solid rgb(232, 224, 216)',
                borderLeft: f.borderLeft,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  marginBottom: 16,
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    backgroundColor: f.iconBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 22,
                    lineHeight: 1,
                  }}
                >
                  {f.emoji}
                </div>
              </div>

              <p
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: 'rgb(17, 17, 17)',
                  lineHeight: 1.4,
                  marginBottom: 8,
                  whiteSpace: 'pre-line',
                  minHeight: '2.8em',
                }}
              >
                {f.title}
              </p>

              <p
                style={{
                  fontSize: 14,
                  color: 'rgb(95, 94, 90)',
                  lineHeight: 1.65,
                  margin: 0,
                  ...(f.id === 'journey'
                    ? {
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }
                    : {}),
                }}
              >
                {f.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
